import { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface IUseLoadMore {
  data: unknown;
  processing: boolean;
  registered: boolean;
  loadMore: () => void;
}

interface IUseLoadMoreProps {
  key: number;
  register: boolean;
  request: () => Promise<unknown>;
  parentRef?: HTMLElement | null;
}

const useLoadMore = ({
  key,
  register,
  request,
  parentRef,
}: IUseLoadMoreProps): IUseLoadMore => {
  const [data, setData] = useState<unknown>(null);
  const [processing, setProcessing] = useState(false);
  const [registered, setRegistered] = useState(false);

  const loadMore = useCallback(() => {
    request()
      .then((res: unknown) => {
        setData(res);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setProcessing(false);
      });
  }, [request]);

  const debounceLoadMore = useDebouncedCallback(loadMore, 500);

  const autoLoad = useCallback(
    (e: Event) => {
      if (processing) return;

      let el = e.target as Document | HTMLElement;

      if (el instanceof Document) {
        el = el.querySelector('html') as HTMLElement;
      }

      if (Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 100) {
        setProcessing(true);
        debounceLoadMore();
      }
    },
    [processing, debounceLoadMore],
  );

  useEffect(() => {
    if (!window) return;

    if (!register) {
      setRegistered(false);

      return;
    }

    setRegistered(true);

    // Default to document
    let currentRef: Document | HTMLElement = window.document;

    if (parentRef) {
      currentRef = parentRef;
    }

    if (currentRef) {
      currentRef.addEventListener('scroll', autoLoad);

      return () => {
        currentRef.removeEventListener('scroll', autoLoad);
      };
    }
  }, [parentRef, autoLoad, key, register]);

  return {
    data,
    processing,
    registered,
    loadMore,
  };
};

export default useLoadMore;
